<template>
    <div class="step">
        <p v-html="step.description"></p>

        <b-alert v-if="(!portal.singlePage || stepIndex === 0) && step.type !== 'CREATION'" id="info-step" class="base-alert h4" show
                 variant="primary">
            <b-icon icon="exclamation-circle-fill"></b-icon>
            Fields with * are mandatory
        </b-alert>

        <b-row>
            <template v-for="field in orderFields(step.fields)">
                <b-col v-if="!field.hidden" v-bind:key="field.position" cols="12" :sm="field.size === 'short' ? 6 : 12" :class="isConditionValid(field, true) ? '' : 'is-empty'">
                    <div v-if="field.type === 'displayText'" v-html="field.description" class="mb-3"></div>

                    <b-form-group :id="getFieldId(field.name, 'group')" :label="field.label"
                                  :label-for="getFieldId(field.name, null)" :class="{'required' : field.required }"
                                  v-if="field.type !== 'checkbox' && field.type !== 'section' && field.type !== 'displayText' && isConditionValid(field, true)">
                        <p v-if="field.description" class="fieldDescription" v-html="field.description"></p>
                        <!-- Text field -->
                        <b-form-input v-if="field.type === 'text'" :id="getFieldId(field.name, null)"
                                      :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                      :required="!!field.required" :maxlength="field.maxLength"/>
                        <!-- Large Text field -->
                        <b-form-textarea v-if="field.type === 'largeText'" :id="getFieldId(field.name, null)"
                                         :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                         :required="!!field.required" :maxlength="field.maxLength"
                                         :formatter="formatMaxLength" trim/>
                        <b-badge v-if="field.maxLength && field.type === 'largeText' && stepValues[field.name]"
                                 :variant="length(stepValues[field.name]) >= field.maxLength ? 'danger' : 'secondary'"
                                 class="float-right input-count rounded">
                            {{ length(stepValues[field.name]) + ' / ' + field.maxLength }}
                        </b-badge>
                        <!-- Number field -->
                        <b-form-input v-if="field.type === 'number'" :id="getFieldId(field.name, null)"
                                      :name="getFieldId(field.name, null)" type="number"
                                      v-model="stepValues[field.name]" :required="!!field.required"/>
                        <!-- Number integer field -->
                        <b-form-input v-if="field.type === 'number_int'" :id="getFieldId(field.name, null)"
                                      :name="getFieldId(field.name, null)" type="number" step="1"
                                      onkeypress='return event.charCode >= 48 && event.charCode <= 57'
                                      v-model="stepValues[field.name]" :required="!!field.required"/>
                        <!-- Date field -->
                        <b-form-input v-if="field.type === 'date'" :id="getFieldId(field.name, null)"
                                      :name="getFieldId(field.name, null)" type="date" v-model="stepValues[field.name]"
                                      :required="!!field.required"/>
                        <!-- Email field -->
                        <b-form-input v-if="field.type === 'email' || field.type === 'confirmEmail'"
                                      :id="getFieldId(field.name, null)" :name="getFieldId(field.name, null)"
                                      type="email"
                                      v-model="stepValues[field.name]" :required="!!field.required"
                                      :data-vv-scope="'step-'+step.position"
                                      v-validate="{required: field.required}" :ref="getFieldId(field.name, null)"/>
                        <!-- Select field -->
                        <b-form-select v-if="checkFieldType(field, ['select', 'selects']) && Object.keys(field.simpleFieldValues).length === 0"
                                       :id="getFieldId(field.name, null)" :name="getFieldId(field.name, null)"
                                       v-model="stepValues[field.name]" :options="orderFields(field.fieldValues)"
                                       :multiple="field.type === 'selects'" :required="!!field.required"
                                       text-field="label"/>
                        <b-form-select v-if="checkFieldType(field, ['select', 'selects']) && Object.keys(field.simpleFieldValues).length !== 0"
                                       :id="getFieldId(field.name, null)" :name="getFieldId(field.name, null)"
                                       v-model="stepValues[field.name]" :options="field.simpleFieldValues"
                                       :multiple="field.type === 'selects'" :required="!!field.required"/>
                        <!-- Radio field -->
                        <b-form-radio-group v-if="field.type === 'radio' && Object.keys(field.simpleFieldValues).length === 0"
                                            :id="getFieldId(field.name, null)"
                                            :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                            :required="!!field.required"
                                            :options="orderFields(field.fieldValues)" text-field="label"
                                            :buttons="field.btnStyled" button-variant="outline-primary"
                                            class="btn-group-custom"/>
                        <b-form-radio-group v-if="field.type === 'radio' && Object.keys(field.simpleFieldValues).length !== 0"
                                            :id="getFieldId(field.name, null)"
                                            :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                            :required="!!field.required"
                                            :options="field.simpleFieldValues"
                                            :buttons="field.btnStyled" button-variant="outline-primary"
                                            class="btn-group-custom"/>
                        <!-- Checkboxes field -->
                        <b-form-checkbox-group v-if="field.type === 'checkboxes' && Object.keys(field.simpleFieldValues).length === 0"
                                               :id="getFieldId(field.name, null)"
                                               :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                               :required="field.required && (!stepValues[field.name] || stepValues[field.name].length <= 0)"
                                               :options="orderFields(field.fieldValues)" text-field="label"
                                               :buttons="field.btnStyled" button-variant="outline-primary"
                                               class="btn-group-custom"/>
                        <b-form-checkbox-group v-if="field.type === 'checkboxes' && Object.keys(field.simpleFieldValues).length !== 0"
                                               :id="getFieldId(field.name, null)"
                                               :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                               :required="field.required && (!stepValues[field.name] || stepValues[field.name].length <= 0)"
                                               :options="orderFields(field.simpleFieldValues)" text-field="label"
                                               :buttons="field.btnStyled" button-variant="outline-primary"
                                               class="btn-group-custom"/>
                        <!-- Document field -->
                        <template v-if="field.type === 'documents'">
                            <b-list-group class="mb-3" v-if="step.type !== 'CREATION' && $parent.files[field.name]">
                                <b-list-group-item v-for="(file, index) in $parent.files[field.name]" :key="index"
                                    class="d-flex justify-content-between align-items-center" :variant="$parent.filesState !== null && !$parent.filesState ? 'danger' : ''">
                                    {{file.name}}
                                    <b-link @click="removeFile(field.name, index)" class="text-danger">
                                        <b-icon icon="x"></b-icon>
                                    </b-link>
                                </b-list-group-item>
                            </b-list-group>
                            <b-form-file multiple
                                         :id="getFieldId(field.name, null)"
                                         :name="getFieldId(field.name, null)"
                                         v-model="tmpFiles"
                                         :required="field.required && (!$parent.files[field.name] || $parent.files[field.name].length < 1)"
                                         @input="addFile($event, field.name)"
                                         :accept="$parent.portal.allowedExtensions"
                                         :state="$parent.filesState"
                                         placeholder="Choose a file or drop it here..."
                                         drop-placeholder="Drop file here..."/>
                            <b-form-invalid-feedback :id="getFieldId(field.name, 'feedback')">
                                <span v-for="(err, index) in $parent.filesErrors" :key="index">{{ err }}<br></span>
                            </b-form-invalid-feedback>
                        </template>
                        <!-- Boolean field -->
                        <b-form-radio-group v-if="field.type === 'booleanBtn'" :id="getFieldId(field.name, null)"
                                            :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                            :required="!!field.required" :options="fieldBooleanBtn"
                                            :buttons="field.btnStyled" button-variant="outline-primary"
                                            class="btn-group-custom"/>

                        <!-- Autocomplete field -->
                        <div v-if="checkFieldType(field, ['autocomplete', 'autocomplete_create'])" class="typeahead">
                            <b-input-group v-if="!stepValues[field.name]">
                                <!-- Hidden field to use v-validate -->
                                <b-form-input v-if="portal.singlePage" v-model="stepValues[field.name]" hidden
                                              :data-vv-name="getFieldId(field.name, null)" :data-vv-as="field.label"
                                              v-validate="{required: field.required}"/>
                                <b-form-input v-if="!portal.singlePage" v-model="stepValues[field.name]" hidden
                                              :data-vv-scope="'step-'+step.position" :ref="getFieldId(field.name, null)"
                                              :data-vv-name="getFieldId(field.name, null)" :data-vv-as="field.label"
                                              v-validate="{required: field.required}"/>
                                <!-- Typeahead -->
                                <vue-bootstrap-typeahead :id="getFieldId(field.name, null)"
                                                         :name="getFieldId(field.name, null)" v-model="queries[field.position]"
                                                         :required="!!field.required" :data="objects[field.position]" :min-matching-chars="3"
                                                         @hit="stepValues[field.name] = $event"
                                                         :serializer="item => item.name ? item.name : item.text" style="flex: 1 1 auto;"/>
                                <b-input-group-append v-if="field.type === 'autocomplete_create'">
                                    <b-button v-if="!portal.leadSpace" @click="showSCModal(field, $event.target)" variant="primary">
                                        <b-icon icon="plus"></b-icon>
                                    </b-button>
                                    <b-button v-if="portal.leadSpace" @click="createObject(field)" variant="primary">
                                        <b-icon icon="plus"></b-icon>
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                            <b-form-text :id="getFieldId(field.position, 'er-feedback')" text-variant="danger"
                                         v-if="!stepValues[field.name] && queries[field.position]">
                                <span>No {{ field.label }} selected</span>
                            </b-form-text>

                            <b-button v-if="stepValues[field.name] && !portal.leadSpace" variant="primary"
                                      :id="getFieldId(field.name, 'select')" :name="getFieldId(field.name, 'select')"
                                      v-model="stepValues[field.name]"
                                      @click="resetSelectOrg(field)"><b-icon icon="check"></b-icon> {{ stepValues[field.name].name}}</b-button>

                            <b-button v-if="stepValues[field.name] && portal.leadSpace" variant="primary"
                                      :id="getFieldId(field.name, 'select')" :name="getFieldId(field.name, 'select')"
                                      v-model="stepValues[field.name]"
                                      @click="resetSelectOrg(field)"><b-icon icon="check"></b-icon> {{ stepValues[field.name].text ? stepValues[field.name].text : stepValues[field.name] }}</b-button>
                        </div>

                        <!-- Autocompletes field -->
                        <div v-if="checkFieldType(field, ['autocompletes', 'autocompletes_create'])">
                            <b-input-group>
                                <!-- Hidden field to use v-validate -->
                                <b-form-input v-if="portal.singlePage" v-model="stepValues[field.name]" hidden
                                              :data-vv-name="getFieldId(field.name, null)" :data-vv-as="field.label"
                                              v-validate="{required: field.required}"/>
                                <b-form-input v-if="!portal.singlePage" v-model="stepValues[field.name]" hidden
                                              :data-vv-scope="'step-'+step.position" :ref="getFieldId(field.name, null)"
                                              :data-vv-name="getFieldId(field.name, null)" :data-vv-as="field.label"
                                              v-validate="{required: field.required}"/>
                                <!-- Typeahead -->
                                <vue-bootstrap-typeahead :id="getFieldId(field.name, null)"
                                                         :name="getFieldId(field.name, null)" v-model="queries[field.position]"
                                                         :required="!!field.required" :data="objects[field.position]" :min-matching-chars="3"
                                                         @hit="addObject(field, $event)"
                                                         :serializer="item => item.name ? item.name : item.text" style="flex: 1 1 auto;"/>
                                <b-input-group-append v-if="field.type === 'autocompletes_create'">
                                    <b-button @click="showSCsModal(field, $event.target)" variant="primary">
                                        <b-icon icon="plus"></b-icon>
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>

                            <div class="mt-2">
                                <template v-for="(obj, index) in stepValues[field.name]">
                                <b-button variant="primary" v-bind:key="index" class="mr-2"
                                          :id="getFieldId(field.name, 'select-' + index)" :name="getFieldId(field.name, 'select')"
                                          @click="removeSelectOrg(field, index)"><b-icon icon="check"></b-icon> {{ obj.name ? obj.name : obj.text }}</b-button>
                                </template>
                            </div>
                        </div>

                        <!-- Field error feedback -->
                        <b-form-text :id="getFieldId(field.name, 'er-feedback')" text-variant="danger"
                                     v-if="portal.singlePage && veeErrors.first(getFieldId(field.name, null))">
                            <span>{{ veeErrors.first(getFieldId(field.name, null)) }}</span>
                        </b-form-text>
                        <b-form-text :id="getFieldId(field.name, 'er-feedback')" text-variant="danger"
                                     v-if="!portal.singlePage && veeErrors.first('step-'+step.position+'.'+getFieldId(field.name, null))">
                            <span>{{ veeErrors.first('step-'+step.position+'.'+getFieldId(field.name, null)) }}</span>
                        </b-form-text>
                    </b-form-group>

                    <b-form-checkbox v-if="field.type === 'checkbox' && isConditionValid(field, true)" :id="getFieldId(field.name, null)"
                                     :name="getFieldId(field.name, null)" v-model="stepValues[field.name]"
                                     :required="!!field.required"
                                     :class="['my-2', {'ckc-required' : field.required }]" :button="field.btnStyled"
                                     button-variant="primary" value="true" unchecked-value="false">
                        <span>
                            <span v-html="field.label"></span>
                            <b-link
                                @click="$parent.showHtmlModal(field.modalTitle ? field.modalTitle : 'Privacy Policy', field.modalHtmlContent)">
                                {{ field.linkText }}</b-link>
                        </span>
                    </b-form-checkbox>
                    <b-alert v-if="field.type === 'section'" :id="getFieldId(field.name, null)" class="base-alert h5" show variant="secondary">
                        <b-icon icon="caret-down-fill"></b-icon>
                        {{ field.label }}
                    </b-alert>
                    <b-form-invalid-feedback :id="getFieldId(field.name, 'feedback')">
                        This field is required.
                    </b-form-invalid-feedback>
                </b-col>
                <b-col v-if="field.type === 'confirmEmail'" v-bind:key="field.position+'confirm'"
                       cols="12" :sm="field.size === 'short' ? 6 : 12">
                    <b-form-group :id="getFieldId(field.name, 'confirm-group')" :label="'Confirm '+field.label"
                                  :label-for="getFieldId(field.name, null)" :class="{'required' : field.required }">
                        <b-form-input :id="getFieldId(field.name, 'confirm')" :name="getFieldId(field.name, 'confirm')"
                                      type="email" v-model="confirmEmail" :required="!!field.required"
                                      :data-vv-scope="'step-'+step.position"
                                      v-validate="{required: true, confirmed: getFieldId(field.name, null)}"
                                      :state="validateState('step-'+step.position, getFieldId(field.name, 'confirm'))"/>
                        <b-form-invalid-feedback :id="getFieldId(field.name, 'confirm-feedback')">
                            The email confirmation does not match.
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>

            </template>
        </b-row>

        <!-- Step creation modal -->
        <b-modal :id="scModal.id" :title="scModal.title" size="lg" ok-title="Submit" centered scrollable
                 @ok="okSCModal(scModal.field)" @hide="resetSCModal">
            <step :portal="portal" :step="scModal.step" :stepValues="scModal.stepValues" :stepIndex="scModal.step.position"/>
        </b-modal>
        <b-modal :id="scsModal.id" :title="scsModal.title" size="lg" ok-title="Add" centered scrollable
                 @ok="okSCsModal(scsModal.field)" @hide="resetSCsModal">
            <step :portal="portal" :step="scsModal.step" :stepValues="scsModal.stepValues" :stepIndex="scsModal.step.position"/>
        </b-modal>

    </div>
</template>

<script>
    import _ from "lodash";
    import VueBootstrapTypeahead from 'vue-bootstrap-typeahead'
    import SubmissionService from "@/services/SubmissionService";

    export default {
        name: "Step",
        components: {
            VueBootstrapTypeahead
        },
        inject: ['$validator'],
        props: {
            portal: Object,
            step: Object,
            stepValues: Object,
            stepIndex: Number,
        },
        data() {
            return {
                confirmEmail: null,
                objects: [],
                queries: [],
                unwatchs: [],
                scModal: {
                    id: `step-${this.stepIndex}-creation-modal`,
                    title: '',
                    field: {},
                    step: {},
                    stepValues: {},
                },
                scsModal: {
                    id: `step-${this.stepIndex}-creations-modal`,
                    title: '',
                    field: {},
                    step: {},
                    stepValues: {},
                },
                fieldBooleanBtn: [
                    {value: true, text: 'Yes'},
                    {value: false, text: 'No'},
                ],
                tmpFiles: []
            }
        },
        methods: {
            getFieldId(field, att) {
                if (att != null) {
                    return this.step.position + '-' + field + '-' + att;
                } else {
                    return this.step.position + '-' + field;
                }
            },
            orderFields(fields) {
                return _.orderBy(fields, 'position')
            },
            validateState(scope, ref) {
                const field = this.$validator.fields.find({name: ref, scope: scope});
                if (field && (field.flags.dirty || field.flags.validated)) {
                    return this.veeErrors.has(ref, scope) ? !this.veeErrors.has(ref, scope) : null;
                }
                return null;
            },
            isConditionValid(field, empty) {
                if (field.condition) {
                    let conditions = field.condition.split(",");
                    // following for loop is for multiple conditions separated by ,
                    for (let condition of conditions) {
                        const fieldName = condition.substring(0, condition.indexOf('='));
                        const conditionValues = condition.substring(condition.indexOf('=') + 1, condition.length);
                        let fieldValue = this.stepValues[fieldName];

                        if (fieldValue != null) {
                            let indexes = [];
                            // Find all configured fields index that match the condition
                            this.step.fields.forEach((item, index) => {
                                if (item.name === fieldName) {
                                    indexes.push(index);
                                }
                            });
                            let hasVal = false;
                            let conditionValuesList = conditionValues.split("|");

                            if (Array.isArray(fieldValue)) {
                                // When the condition field value is an array (multiple value selected)
                                for (let fv of fieldValue) {
                                    //  Condition field value selected
                                    let fieldValueStr = this.getConditionFieldValueString(indexes, fv);
                                    if (conditionValuesList.includes(fv) || conditionValuesList.includes(fieldValueStr)) {
                                        hasVal = true;
                                    }
                                }
                            } else {
                                //  Condition field value selected
                                let fieldValueStr = this.getConditionFieldValueString(indexes, fieldValue);
                                if (conditionValuesList.includes(fieldValue) || conditionValuesList.includes(fieldValueStr)) {
                                    hasVal = true;
                                }
                            }
                            if (!hasVal) {
                                // Empty current field value if not displayed
                                if (empty) {
                                    this.emptyCurrentField(field);
                                }
                                return false;
                            }
                        } else {
                            // Empty current field value if not displayed
                            if (empty) {
                                this.emptyCurrentField(field);
                            }
                            return false;
                        }
                    }
                }
                return true;
            },
            getConditionFieldValueString(indexes, fv) {
                let fieldValueStr = null;
                for (let fieldIndex of indexes) {
                    // Configured field index
                    let isFieldValues = this.step.fields[fieldIndex].fieldValues && this.step.fields[fieldIndex].fieldValues[fv];
                    if (!fieldValueStr && isFieldValues) {
                        fieldValueStr = this.step.fields[fieldIndex].fieldValues[fv].label;
                    }
                }
                return fieldValueStr ? fieldValueStr.toString() : fv.toString();
            },
            emptyCurrentField(field) {
                const duplicates = this.step.fields.filter(f => f.name === field.name && f.position !== field.position);
                let isValid = false;
                // Check if duplicate(s) field has a valid condition (= is displayed)
                if (duplicates) {
                    for (let duplicate of duplicates) {
                        isValid = this.isConditionValid(duplicate, false);
                        if (isValid) {
                            break;
                        }
                    }
                }
                // Only empty a field when every duplicates have an invalid display condition
                // (= Field never displayed)
                if (!isValid) {
                    switch (field.type) {
                        case "selects" :
                        case "checkboxes":
                        case "autocompletes":
                        case "autocompletes_create":
                            if (this.stepValues[field.name] && this.stepValues[field.name].length > 0) {
                                this.stepValues[field.name] = [];
                            }
                            break;
                        case "documents":
                            if ((this.$parent.files[field.name] && Object.keys(this.$parent.files[field.name]).length !== 0)
                                || (this.stepValues[field.name] && Object.keys(this.stepValues[field.name]).length !== 0)) {
                                this.$parent.files[field.name] = [];
                                this.stepValues[field.name] = [];
                                this.$parent.filesState = null;
                            }
                            break;
                        default:
                            if (this.stepValues[field.name]) {
                                this.stepValues[field.name] = null;
                            }
                    }
                }
            },
            length(value) {
                let encoder = new TextEncoder('utf-8'),
                    encodedMessage;
                encodedMessage = encoder.encode(value.replace(/(\r\n|\n|\r)/g, "  "));
                return encodedMessage.length;
            },
            formatMaxLength(value, evt) {
                if (evt && evt.target) {
                    let maxLength = evt.target.maxLength;
                    if (value != null && maxLength && maxLength > 0) {
                        let length = this.length(value);
                        let exceedingChar = length - maxLength;
                        while (exceedingChar > 0 && length > 0) {
                            value = value.substring(0, value.length - 1);
                            length = this.length(value);
                            exceedingChar = length - maxLength;
                        }
                        return value;
                    }
                }
                return value;
            },
            resetSelectOrg(field) {
                this.stepValues[field.name] = null;
                this.resetAutocomplete(field)
            },
            removeSelectOrg(field, index) {
                this.$delete(this.stepValues[field.name], index);
                this.resetAutocomplete(field)
            },
            resetAutocomplete(field) {
                this.queries[field.position] = '';
                this.$forceUpdate();
            },
            showSCModal(field, button) {
                this.scModal.title = field.stepCreation.name;
                this.scModal.step = field.stepCreation;
                this.scModal.field = field;
                if (!this.scsModal.stepValues) {
                    this.scsModal.stepValues = {};
                }
                this.$root.$emit('bv::show::modal', this.scModal.id, button)
            },
            okSCModal(field) {
                this.stepValues[field.name] = this.scModal.stepValues;
                this.scModal.stepValues = null;
            },
            resetSCModal() {
                this.scModal.title = '';
                this.scModal.step = {};
                this.scModal.stepValues = {};
            },
            showSCsModal(field, button) {
                this.scsModal.title = field.stepCreation.name;
                this.scsModal.step = field.stepCreation;
                this.scsModal.field = field;
                if (!this.scsModal.stepValues) {
                    this.scsModal.stepValues = {};
                }
                this.$root.$emit('bv::show::modal', this.scsModal.id, button)
            },
            okSCsModal(field) {
                this.addObject(field, this.scsModal.stepValues);
                this.scsModal.stepValues = null;
            },
            resetSCsModal() {
                this.scsModal.title = '';
                this.scsModal.step = {};
                this.scsModal.stepValues = {};
            },
            addObject(field, event) {
                if (!this.stepValues[field.name]) {
                    this.stepValues[field.name] = [];
                }
                this.stepValues[field.name].push(event);
                this.resetAutocomplete(field);
            },
            removeFile(name, index) {
                this.$parent.files[name].splice(index, 1);
                this.$parent.filesState = null;
                this.$forceUpdate();
            },
            addFile(event, name) {
                if (!this.$parent.files[name]) {
                    this.$parent.files[name] = [];
                }
                if (!this.stepValues[name]) {
                    this.stepValues[name] = [];
                }
                event.forEach(file => {
                    this.$parent.files[name].push(file);
                    this.stepValues[name].push(file.name)
                });
                this.tmpFiles = [];
            },
            checkFieldType(field, types) {
                if (types.includes(field.type)) {
                    if (!this.stepValues[field.name] && field.type === "selects") {
                        this.stepValues[field.name] = [];
                    } else if (field.type === "autocomplete" || field.type === "autocomplete_create" || field.type === "autocompletes" || field.type === "autocompletes_create") {
                        if (!this.queries[field.position]) {
                            this.queries[field.position] = '';
                            this.objects[field.position] = [];
                        }
                        if (!this.unwatchs[field.position]) {
                            console.log("watch", field.position)
                            this.unwatchs[field.position] = this.$watch(() => this.queries[field.position], (newQuery) => {
                                this.handleQueryChange(newQuery, field);
                            }, {deep: true});
                        }
                    }
                    return true
                }
            },
            createObject(field) {
                this.stepValues[field.name] = this.queries[field.position];
                this.$forceUpdate();
            },
            handleQueryChange(newQuery, field) {
                if (newQuery && newQuery.length > 2) {
                    SubmissionService.findObjectsByQuery(this.portal.client.id, field.linkedClass, newQuery, this.portal.leadSpace)
                        .then(response => {
                            this.objects[field.position] = response.data;
                            this.$forceUpdate();
                        })
                        .catch(error => {
                            console.log(error)
                        });
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
    .input-count {
        margin-top: -25px;
        margin-right: 5px;
        opacity: 0.99;
    }
</style>